<template>
    <div class="flex justify-between">
        <h5 class="text-sm font-medium mb-2">{{ libraryTitle }}</h5>
        <button
            class="border border-gray-400 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded"
            @click="openUploadDrawer">
            {{ uploadNewFileText }}
        </button>
    </div>
    <div class="flex flex-col relative max-h-[70vh]">

        <!-- Pole wyszukiwania - fixed na górze -->
        <div v-if="images.length > 0 && !loadingImages" class="mb-4 p-1 flex-shrink-0">
            <input
                type="text"
                v-model="searchQuery"
                :placeholder="filterLogotypesText"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
            />
        </div>

        <!-- Scrollowalna część środkowa -->
        <div ref="galleryScrollContainer" class="flex-1 min-h-0 overflow-y-auto">
            <div v-if="filteredImages.length > 0 && !loadingImages" class="my-6" :class="uploadVisible ? '' : ''">
                <div
                    class="grid grid-cols-4 gap-4"
                >
                    <div
                        v-for="(image, index) in filteredImages"
                        :key="image.id"
                        class="border border-gray-300 rounded-md p-2 flex flex-col max-h-[144px] mb-2"
                        :class="{
                'border-lime-500 shadow-md shadow-lime-500/50': isSelected(image),
                'opacity-50 grayscale cursor-not-allowed': isInvalidExtension(image),
                'opacity-50 cursor-not-allowed': !isInvalidExtension(image) && !isSelected(image) && selectedImages.length >= 1,
                'border-gray-300 hover:border-gray-500 hover:shadow-gray-500/50 hover:shadow-md': !isSelected(image) && !isInvalidExtension(image),
                'cursor-pointer hover:opacity-100' : !isInvalidExtension(image)
              }"
                        @click="selectPhoto(image)"
                        @mouseenter="onGalleryItemEnter($event, image)"
                        @mouseleave="hideInvalidExtensionTooltip"
                    >
                        <img
                            :src="image.thumb_url"
                            :alt="image.name"
                            class="w-full h-24 object-contain rounded"
                        />
                        <div class="flex justify-between">
                            <p class="image-name-space text-sm mt-2 whitespace-nowrap overflow-hidden">{{
                                    image.file_name
                                }}</p>
                            <button
                                :disabled="isSelected(image)"
                                @click="handleDeleteClick($event, image)"
                                class="text-red-500 hover:text-red-600 mt-2 z-[9999]"
                                :class="{
                            'pointer-events-none opacity-50': isSelected(image)
                        }"
                            >
                                <IconTrash/>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex flex-col items-center justify-center"
                 v-else-if="images.length == 0 && !loadingImages">
                <ImgNoFilesInLibrary></ImgNoFilesInLibrary>
                <p class="text-gray-500 mt-2">{{ noFilesInLibraryText }}</p>
            </div>
            <div class="flex items-center justify-center text-gray-500"
                 v-else-if="images.length > 0 && filteredImages.length == 0 && !loadingImages">
                <p>{{ noSearchResultsText }}</p>
            </div>
            <div class="flex items-center justify-center"
                 v-else
            >
                <svg
                    class="h-5 w-5 mr-2 inline-block text-white-500 custom-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    />
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.372 0 0 5.372 0 12h4z"
                    />
                </svg>
            </div>
        </div>
        <div class="right-drawer h-full"
             :style="{
     width: uploadVisible ? '25vw' : '0',
     paddingLeft: uploadVisible ? '10px' : '0',
  }"
             @onClickOutside="uploadVisible = false"
        >
            <div class="h-1/6 flex items-center justify-between">
                <button
                    @click="uploadDrawerOpen = !uploadDrawerOpen; uploadVisible = false"
                    class="flex items-center text-gray-600 hover:text-gray-900 text-lg"
                >
                    <IconChevronLeft
                        class="mr-2"
                    ></IconChevronLeft>
                    <span>{{ uploadNewFileText2 }}</span>
                </button>
            </div>

            <div v-if="invalidExtension || uploadGradientError || uploadBitmapError" class="ml-2 p-2 text-red-500 text-sm font-semibold">
                <span v-if="invalidExtension">{{ unsupportedFormatText }}</span>
                <span v-else-if="uploadBitmapError">{{ bitmapErrorText }}</span>
                <span v-else>{{ gradientErrorText }}</span>
            </div>
            <div
                v-if="!fileUpload"
                class="h-2/3 text-center border-2 border-dashed border-gray-300 rounded-lg p-6 mx-4 flex items-center justify-center"
                :class="dragOver ? 'border-blue-500' : 'border-gray-300 cursor-pointer'"
                @dragover.prevent="handleDragOver"
                @dragleave.prevent="handleDragLeave"
                @drop.prevent="handleDrop"
                @click="selectFile"
            >
                <div>
                    <p class="font-semibold text-gray-700 mb-2">
                        {{ uploadFileText }} <span class="text-gray-500">{{ orText }}</span> {{ dropHereText }}
                    </p>
                    <p class="text-sm text-gray-500 mb-4">
                        {{ maxFileSizeText }}: 15MB<br/>
                        {{ allowedFormatsText }}: {{ extensions.join(', ') }}
                    </p>
                    <input
                        type="file"
                        :accept="extensions.join(',')"
                        id="fileInput"
                        ref="fileInput"
                        @change="handleFileSelect"
                        class="hidden"
                    />
                    <button
                        @click="triggerFileInput"
                        class="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800"
                    >
                        {{ uploadFileText }}
                    </button>
                </div>
            </div>
            <div class="h-2/3" v-else>
                <div>
                    <div class="border rounded-md p-4 flex items-center mb-4">
                        <!-- Loader podczas przetwarzania pliku -->
                        <div v-if="filePreviewLoading" class="flex items-center justify-center w-12 h-[60px]">
                            <svg
                                class="h-8 w-8 animate-spin text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"
                                />
                                <path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.372 0 0 5.372 0 12h4z"
                                />
                            </svg>
                        </div>
                        <!-- Podgląd obrazu -->
                        <div v-else-if="isImage && !filePreviewLoading">
                            <img
                                v-if="filePreview"
                                :src="filePreview"
                                alt="Uploaded file preview"
                                class="w-12 max-h-[60px]"
                            />
                        </div>
                        <!-- Podgląd PDF -->
                        <div v-else-if="!isImage && !filePreviewLoading">
                            <PdfViewer
                                v-if="filePreview"
                                :src="filePreview"
                                :width="48"
                                :height="48"
                                :max-width="48"
                                :max-height="48"
                                :scale="1"
                                :page="1"
                            />
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="ml-2 text-sm font-medium truncate" :title="fileUpload.name">{{
                                    fileUpload.name
                                }}</p>
                        </div>
                        <button
                            @click="cancelAddingImage"
                            class="text-red-500 hover:text-red-700"
                        >
                            <IconTrash/>
                        </button>
                    </div>

                    <div class="flex flex-col items-start gap-2 w-full">
                        <div class="flex items-center">
                            <input
                                id="add-to-library"
                                type="checkbox"
                                v-model="addToLibrary"
                                class="h-4 w-4 text-white accent-lime-500 focus:ring-green-500 border-white rounded"
                            />
                            <label
                                for="add-to-library"
                                class="ml-2 text-sm text-gray-600"
                            >
                                {{ addToLibraryText }}
                            </label>
                        </div>

                        <div
                            v-if="toastVisible"
                            class="w-full mr-2 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2"
                            role="alert"
                        >
                            <svg
                                class="h-4 w-4 flex-shrink-0 text-red-500 mt-[2px]"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                            >
                                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <p class="text-sm whitespace-pre-line leading-snug">
                                {{ toastMessage }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="h-1/3 flex justify-between p-6 gap-2">
                <button
                    @click="uploadVisible = false; filePreviewLoading = false"
                    class="w-1/2 max-h-[45px] px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                >
                    {{ cancelText }}
                </button>
                <button
                    class="w-1/2 max-h-[45px] px-2 py-2 rounded custom-line-height"
                    :class="fileUpload && !processLoading ? 'text-white bg-gray-800 cursor-pointer hover:bg-gray-600' : 'text-gray-400 bg-gray-100'"
                    :disabled="!fileUpload || processLoading"
                    @click="checkImageQuality"
                >
                    {{ addImageText }}
                    <svg
                        v-if="processLoading"
                        class="ml-2 h-5 w-5 mr-2 inline-block text-white-500 custom-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        />
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.372 0 0 5.372 0 12h4z"
                        />
                    </svg>
                </button>
            </div>
        </div>
        <div v-if="libraryGradientError || libraryBitmapError" class="px-4 pb-2 text-red-500 text-sm font-semibold flex-shrink-0">
            <span v-if="libraryBitmapError">{{ bitmapErrorText }}</span>
            <span v-else>{{ gradientErrorText }}</span>
        </div>
        <div class="flex justify-end p-4 gap-2 flex-shrink-0">
            <button
                @click="closeGalleryModal"
                class="max-h-[45px] w-48 px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-100 "
                :class="uploadVisible ? 'invisible' : 'visible'"
            >
                {{ cancelText2 }}
            </button>
            <button
                class="max-h-[45px] w-50 px-4 py-2 rounded"
                :disabled="selectedImages.length === 0 || processCheckQualityLoading"
                :class="(fileUpload || selectedImages.length > 0) && processCheckQualityLoading === false? 'text-white bg-gray-800 cursor-pointer hover:bg-gray-600' : 'text-gray-400 bg-gray-100'"
                @click="libraryCheckImageQuality"
            >
                {{ addImageText2 }}
                <svg
                    v-if="processCheckQualityLoading"
                    class="ml-2 h-5 w-5 mr-2 inline-block text-white-500 custom-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    />
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.372 0 0 5.372 0 12h4z"
                    />
                </svg>
            </button>
        </div>
    </div>
    <Modal
        v-model="qualityCheckModalOpened"
        :title="qualityCheckTitle"
        :size="'md'">
        <div>
            <p class="text-sm text-gray-600 mb-6">
                {{ qualityCheckMessage }}
            </p>
        </div>
        <div v-if="showCmykConversionNotice" class="mb-6">
            <div class="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-4 py-3">
                <div class="flex-shrink-0">
                    <div class="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                        <span class="text-white text-sm font-bold">!</span>
                    </div>
                </div>
                <p class="text-sm leading-snug">
                    {{ cmykConversionMessage }}
                </p>
            </div>
        </div>
        <div class="border border-gray-400 rounded">
            <div v-if="isImage"
                 class="p-6 mx-4 flex justify-center"
            >
                <img
                    v-if="filePreview"
                    :src="filePreview"
                    alt="Uploaded file preview"
                    class="w-auto max-h-[50vh]"
                />
            </div>
            <div v-else
                 class="p-6 mx-4 flex items-center justify-center"
            >
                <!-- Loader podczas ładowania podglądu PDF w modal -->
                <div v-if="processLoading" class="flex items-center justify-center h-[50vh]">
                    <svg
                        class="h-12 w-12 animate-spin text-gray-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        />
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.372 0 0 5.372 0 12h4z"
                        />
                    </svg>
                </div>
                <!-- Podgląd po załadowaniu -->
                <img
                    v-else-if="qualityCheckPreview"
                    :src="qualityCheckPreview"
                    alt="Uploaded file preview"
                    class="w-auto max-h-[50vh]"
                />
                <!--                <iframe-->
                <!--                    v-if="qualityCheckPreview"-->
                <!--                    style="border:1px solid #666CCC"-->
                <!--                    :src="qualityCheckPreview + '#toolbar=0&navpanes=0&statusbar=0&messages=0&scrollbar=0'"-->
                <!--                    class="w-full h-auto aspect-square"-->
                <!--                />-->
            </div>
        </div>

        <div>
            <div class="flex justify-between text-xs text-gray-600 my-2">
                <span :class="qualityMark == 'bad' ? 'text-red-600 font-medium' : ''">{{ badQualityText }}</span>
                <span :class="qualityMark == 'okay' ? 'text-orange-600 font-medium' : ''">{{ okayQualityText }}</span>
                <span :class="qualityMark == 'good' ? 'text-green-600 font-medium' : ''">{{ goodQualityText }}</span>
            </div>
            <div class="w-full bg-gray-200 h-2 rounded-full flex items-center">
                <div
                    class="h-2 bg-green-600 rounded-full"
                    :style="{ width: qualityScore + '%' }"
                ></div>
            </div>
            <div class="flex justify-center">
                <span
                    class="text-sm text-gray-700"
                >
                  {{ qualityScore }}%
                </span>
            </div>

            <div v-if="(printMethod.startsWith('L') || printMethod.startsWith('TC')) && logoColorsCount > 1" class="flex justify-center space-x-2 mt-2">
                <div class="flex items-center gap-3 bg-blue-50 p-4 rounded-lg mb-4">
                    <div class="flex-shrink-0">
                        <div class="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                            <span class="text-white text-sm font-bold">!</span>
                        </div>
                    </div>
                    <span class="text-sm">{{ graverColorReplaceMessage }}</span>
                </div>
            </div>
        </div>

        <div class="flex justify-between mt-6">
            <button
                class="px-4 py-2 bg-gray-200 text-gray-600 rounded hover:bg-gray-300"
                @click="checkQualityFromLibrary = false;qualityCheckModalOpened = false"
            >
                {{ backToLibraryText }}
            </button>
            <button
                class="px-4 py-2 rounded"
                :class="!processLoadingSave ? 'text-white bg-gray-800 cursor-pointer hover:bg-gray-600' : 'text-gray-400 bg-gray-100'"
                :disabled="processLoadingSave"
                @click="handleUpload"
            >
                {{ qualityModalConfirmText }}
                <svg
                    v-if="processLoadingSave"
                    class="ml-2 h-5 w-5 mr-2 inline-block text-white-500 custom-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    />
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.372 0 0 5.372 0 12h4z"
                    />
                </svg>
            </button>
        </div>
    </Modal>

    <Modal
        v-model="deleteImageModal"
        :title="deleteImageTitle"
        :size="'sm'">
        <div
            class="flex items-center mb-4 mt-8"
        >
            <div
                class="text-sm mt-3"
            >
                <p>{{ deleteImageMessage }}</p>
            </div>
        </div>
        <div
            class="flex space-x-4 w-full mt-8"
        >
            <button
                class="w-1/2 px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                @click="deleteImageModal = false"
            >
                {{ deleteImageCancelText }}
            </button>
            <button
                class="w-1/2 px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-600"
                @click="deleteImage()"
            >
                {{ deleteImageConfirmText }}
            </button>
        </div>
    </Modal>
    <Teleport to="body">
        <div
            v-if="invalidExtensionTooltipVisible"
            class="fixed z-[99999] pointer-events-none px-3 py-2 text-sm leading-snug text-white bg-gray-800 rounded-md shadow-lg max-w-[300px] text-center"
            :style="invalidExtensionTooltipStyle"
        >
            {{ invalidExtensionTooltipText }}
            <span
                class="absolute left-1/2 -translate-x-1/2 border-[6px] border-transparent"
                :class="invalidExtensionTooltipBelow ? 'bottom-full border-b-gray-800' : 'top-full border-t-gray-800'"
            ></span>
        </div>
    </Teleport>
</template>

<script setup>
import {computed, onMounted, ref, watch, onBeforeUnmount, defineAsyncComponent} from "vue"
import Modal from "../Modal.vue";
import ImgNoFilesInLibrary from '@/components/icons/ImgNoFilesInLibrary'
import IconChevronLeft from "../icons/IconChevronLeft.vue"
import IconTrash from "../icons/IconTrash.vue";
import {useTranslation} from '../../composables/useTranslation.js';

// Lazy load PdfViewer to reduce initial bundle size (pdfjs-dist is ~3-4MB)
const PdfViewer = defineAsyncComponent(() => import('../PdfViewer.vue'));


const getCurrentPrintMethod = () => new URLSearchParams(window.location.search).get('print_method') ?? 'TF1'

const uploadVisible = ref(false)
const uploadDrawerOpen = ref(false)
const dragOver = ref(false)
const fileInput = ref(null)
const fileUpload = ref(null)
const filePreview = ref(null)
const qualityCheckPreview = ref(null)
const detectedColors = ref([])
const isImage = ref(false)

const qualityCheckModalOpened = ref(false)
const qualityScore = ref(80)
const printMethod = ref(getCurrentPrintMethod())
const logoColorsCount = ref(0)
const colorProfile = ref(null)
const uploadGradientError = ref(false)
const libraryGradientError = ref(false)
const uploadBitmapError = ref(false)
const libraryBitmapError = ref(false)
const uploadIsBitmapPdf = ref(false)
const isRasterImage = (file) => {
    if (!file) return false;

    const rasterExtensions = ['png', 'jpg', 'jpeg', 'bmp', 'gif', 'tiff', 'tif', 'webp'];
    const rasterMimeTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/bmp', 'image/gif', 'image/tiff', 'image/webp'];

    // Sprawdź rozszerzenie pliku
    const fileName = file.name || '';
    const fileExtension = fileName.slice((fileName.lastIndexOf(".") - 1 >>> 0) + 1).toLowerCase();

    // Sprawdź MIME type
    const fileType = file.type || '';

    return rasterExtensions.includes(fileExtension) || rasterMimeTypes.includes(fileType);
}

const allowsRasterUpload = computed(() => {
    if (!Array.isArray(props.extensions) || props.extensions.length === 0) {
        return false
    }

    const rasterMarkers = ['jpg', 'jpeg', 'tiff', 'tif', 'psd', 'png', 'bmp', 'gif', 'webp']

    return props.extensions.some((ext) => {
        const normalized = String(ext).replace('.', '').toLowerCase()
        return rasterMarkers.includes(normalized)
    })
})

const isBitmapPdfMedia = (image) => {
    const flag = image?.custom_properties?.is_bitmap_pdf
    return flag === true || flag === 1 || flag === '1'
}

const isRasterLikeUpload = (file, previewIsBitmap = false) => {
    if (!file) {
        return false
    }

    return isRasterImage(file) || (previewIsBitmap && allowsRasterUpload.value)
}

const hasBitmapBlockError = (result) => {
    return !!result?.isBitmap && !allowsRasterUpload.value
}

const showBitmapNotAllowed = (target) => {
    hideToast()
    uploadBitmapError.value = target === 'upload'
    libraryBitmapError.value = target === 'library'
}

const isDigitalPrint = computed(() => {
    return printMethod.value && (
        printMethod.value.startsWith('H') ||
        printMethod.value.startsWith('D') ||
        printMethod.value.startsWith('FC') ||
        printMethod.value.startsWith('UV') ||
        printMethod.value.startsWith('PL') ||
        printMethod.value === 'TF3A' ||
        printMethod.value === 'SL'
    );
})

const images = ref([])
const loadingImages = ref(false)
const invalidExtension = ref(false)
const processLoading = ref(false)
const checkQualityFromLibrary = ref(false)
const processLoadingSave = ref(false)
const processCheckQualityLoading = ref(false)
const deleteImageModal = ref(false)
const imageIdToDelete = ref(null)
const addToLibrary = ref(false)
const selectedImage = ref(null)
const selectedImages = ref([])
const filePreviewLoading = ref(false)
const searchQuery = ref('')
const toastVisible = ref(false)
const toastMessage = ref('')
let toastTimer = null
const cmykUploadWarningAccepted = ref(false)

const {$t} = useTranslation()

// Reactive translations
const libraryTitle = ref('')
const uploadNewFileText = ref('')
const uploadNewFileText2 = ref('')
const unsupportedFormatText = ref('')
const uploadFileText = ref('')
const orText = ref('')
const dropHereText = ref('')
const maxFileSizeText = ref('')
const gradientErrorText = ref('')
const bitmapErrorText = ref('')
const allowedFormatsText = ref('')
const addToLibraryText = ref('')
const addImageText = ref('')
const addImageText2 = ref('')
const addImageText3 = ref('')
const cancelText = ref('')
const cancelText2 = ref('')
const deleteImageTitle = ref('Usuwanie grafiki')
const deleteImageMessage = ref('Czy na pewno chcesz usunąć grafikę z Twojej biblioteki?')
const deleteImageCancelText = ref('Anuluj')
const deleteImageConfirmText = ref('Usuń')
const qualityCheckTitle = ref('')
const backToLibraryText = ref('')
const badQualityText = ref('')
const okayQualityText = ref('')
const goodQualityText = ref('')
const goodQualityMessage = ref('')
const okayQualityMessage = ref('')
const badQualityMessage = ref('')
const graverColorReplaceMessage = ref('')
const cmykConversionMessage = ref('')
const filterLogotypesText = ref('')
const noFilesInLibraryText = ref('')
const noSearchResultsText = ref('')
const continueText = ref('')
const invalidExtensionTooltipTemplate = ref('Dla wybranej metody nadruku dopuszczone rozszerzenia to: {extensions}')
const galleryScrollContainer = ref(null)
const invalidExtensionTooltipVisible = ref(false)
const invalidExtensionTooltipBelow = ref(false)
const invalidExtensionTooltipStyle = ref({})

const props = defineProps({
    uuid: {
        type: String,
        required: false
    },
    extensions: {
        type: Array,
        default: [], // Wartość domyślna
    },
    userId: {
        type: String,
        required: false
    },
    removeLogo: {
        type: Number,
        default: 0,
    },
});

const currentUserId = ref(null)

const isDigitalPrintMethod = (method = '') => {
    return !!method && (
        method.startsWith('H') ||
        method.startsWith('D') ||
        method.startsWith('FC') ||
        method.startsWith('UV') ||
        method.startsWith('PL') ||
        method === 'TF3A' ||
        method === 'SL'
    )
}

const showCmykConversionNotice = computed(() => {
    return isDigitalPrintMethod(printMethod.value) && (
        !!colorProfile.value?.requires_cmyk_conversion ||
        cmykUploadWarningAccepted.value
    )
})

const qualityModalConfirmText = computed(() => {
    return showCmykConversionNotice.value ? continueText.value : addImageText3.value
})

const fetchUserIdFromVisualization = async () => {
    if (props.userId) {
        currentUserId.value = props.userId
        return
    }

    if (props.uuid) {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const params = new URLSearchParams({
                uuid: props.uuid,
            });

            const response = await fetch(`/api/product?${params}`)
            if (response.ok) {
                const responseData = await response.json()
                if (responseData.visualization && responseData.visualization.user_id) {
                    currentUserId.value = responseData.visualization.user_id
                    return
                }
            }
        } catch (error) {
            console.error('Error fetching visualization:', error)
        }
    }

    const urlParams = new URLSearchParams(window.location.search);
    currentUserId.value = urlParams.get('user_id') ?? "SV4YJ3uuGa"
}

const hideToast = () => {
    if (toastTimer) {
        clearTimeout(toastTimer)
        toastTimer = null
    }
    toastVisible.value = false
    toastMessage.value = ''
}

const showToast = (message) => {
    hideToast()
    toastMessage.value = Array.isArray(message) ? message.join('\n') : message
    toastVisible.value = true
}

onBeforeUnmount(() => {
    hideToast()
    hideInvalidExtensionTooltip()
    galleryScrollContainer.value?.removeEventListener('scroll', hideInvalidExtensionTooltip)
})

const fetchImages = async () => {
    loadingImages.value = true

    // Upewnij się, że mamy user_id
    if (!currentUserId.value) {
        await fetchUserIdFromVisualization()
    }

    try {
        const response = await fetch('/api/user/files?userId=' + currentUserId.value + '&print_method=' + encodeURIComponent(getCurrentPrintMethod()))
        if (!response.ok) {
            throw new Error(`Status: ${response.status}`)
        }
        const responseData = await response.json()
        images.value = responseData
    } catch (error) {
        console.error('Error: ', error)
    } finally {
        loadingImages.value = false
    }
}

const selectFile = () => {
    fileInput.value.click()
}

const revokeBlobUrl = (url) => {
    if (url && typeof url === 'string' && url.startsWith('blob:')) {
        URL.revokeObjectURL(url)
    }
}

const withCacheBuster = (url) => {
    if (!url || (typeof url === 'string' && url.startsWith('blob:'))) {
        return url
    }
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}_t=${Date.now()}`
}

const resetUploadPreviews = ({keepFileUpload = false, closeQualityModal = true} = {}) => {
    revokeBlobUrl(filePreview.value)
    revokeBlobUrl(qualityCheckPreview.value)
    filePreview.value = null
    qualityCheckPreview.value = null

    if (!keepFileUpload) {
        fileUpload.value = null
        if (fileInput.value) {
            fileInput.value.value = ''
        }
    }

    detectedColors.value = []
    qualityScore.value = 80
    logoColorsCount.value = 0
    uploadGradientError.value = false
    uploadBitmapError.value = false
    uploadIsBitmapPdf.value = false
    colorProfile.value = null
    cmykUploadWarningAccepted.value = false
    filePreviewLoading.value = false
    processLoading.value = false
    processCheckQualityLoading.value = false
    checkQualityFromLibrary.value = false
    isImage.value = false
    invalidExtension.value = false
    addToLibrary.value = false

    if (closeQualityModal) {
        qualityCheckModalOpened.value = false
    }
}

const processFile = async (file) => {
    resetUploadPreviews({keepFileUpload: true, closeQualityModal: true})
    libraryBitmapError.value = false
    fileUpload.value = file
    colorProfile.value = null
    cmykUploadWarningAccepted.value = false
    uploadGradientError.value = false
    uploadBitmapError.value = false
    uploadIsBitmapPdf.value = false
    const fileType = file.type

    if (["image/png", "image/jpeg", "image/bmp"].includes(fileType)) {
        isImage.value = true
        filePreviewLoading.value = false
        filePreview.value = URL.createObjectURL(file)
    } else if (fileType === "image/svg+xml") {
        // SVG: bez konwersji, ale przechodzi przez process-preview,
        // żeby sprawdzić gradient (te same zasady co PDF: cyfrowe/niecyfrowe)
        isImage.value = true
        filePreviewLoading.value = true
        await processFileForPreview(file)
    } else {
        isImage.value = false
        filePreviewLoading.value = true

        // Dla plików PDF i innych - wyślij do API przed wyświetleniem
        await processFileForPreview(file)
    }


    // else if (["application/pdf", "application/postscript"].includes(fileType)) {
    //     isImage.value = false
    //     filePreview.value = URL.createObjectURL(file)
    // } else {
    //     filePreview.value = null
    //     isImage.value = false
    //     fileUpload.value = false
    //     alert("Nieobsługiwany format pliku.")
    // }
}

const handleDragOver = () => {
    dragOver.value = true
}

const handleDragLeave = () => {
    dragOver.value = false
}

const handleDrop = (event) => {
    dragOver.value = false
    const {files} = event.dataTransfer
    if (files.length > 0) {
        processFile(files[0])
    }
}

const handleFileSelect = (event) => {

    const acceptedExtensions = props.extensions;
    const {files} = event.target
    let file = null;
    if (files.length > 0) {
        file = files[0];
    }

    if (file) {
        const fileExtension = file.name.slice((file.name.lastIndexOf(".") - 1 >>> 0) + 1);

        if (!acceptedExtensions.includes(`${fileExtension.toLowerCase()}`)) {
            invalidExtension.value = true;
        } else {
            invalidExtension.value = false;
            processFile(file)
        }
    }

}

const isSelected = (photo) => selectedImages.value.some((selected) => selected.id === photo.id)

const handleUpload = async (bitmapErrorTarget = 'upload') => {
    if (checkQualityFromLibrary.value) {
        await colorDetection()
    }

    if (!currentUserId.value) {
        await fetchUserIdFromVisualization()
    }

    const urlParams = new URLSearchParams(window.location.search);



    let variantsArray = urlParams.get('variants');
    if (variantsArray) {
        variantsArray = variantsArray.replaceAll(" ", "").split(',');
    }else{
        variantsArray = [];
    }

    let erpCode = urlParams.get('erp_code') ?? 'V4201-19';
    if (variantsArray.length > 0) {
        erpCode = variantsArray[0];
    }


    processLoadingSave.value = true;
    const formData = new FormData()
    formData.append('file', fileUpload.value)
    formData.append('userId', currentUserId.value)
    formData.append('colors', JSON.stringify(detectedColors.value.colors))
    formData.append('uuid', props.uuid)
    formData.append('erp_code', erpCode)
    formData.append('print_place_name', urlParams.get('print_place_name') ?? 'panel 1')
    formData.append('print_place_size', urlParams.get('print_place_size') ?? '200x120')
    formData.append('print_method', urlParams.get('print_method') ?? 'TF1')
    if (isRasterLikeUpload(fileUpload.value, uploadIsBitmapPdf.value) && cmykUploadWarningAccepted.value) {
        formData.append('skip_cmyk', '1')
    }

    if (checkQualityFromLibrary.value || isRasterLikeUpload(fileUpload.value, uploadIsBitmapPdf.value)) {
        if (selectedImages.value[0]?.id) {
            formData.append('media_id_to_remove', selectedImages.value[0].id)
        }
        checkQualityFromLibrary.value = false;
    }

    if (!addToLibrary.value) {
        formData.append('collection', 'visualisator-hidden')
    }

    // TODO: proper get user_id
    try {
        const response = await fetch("/api/user/files", {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            // obsługa walidacji 422
            if (response.status === 422) {
                const data = await response.json();
                if (data.code === 'CMYK_REQUIRED') {
                    cmykUploadWarningAccepted.value = true
                    cmykConversionMessage.value = data.message || cmykConversionMessage.value
                    showToast(data.message || cmykConversionMessage.value)
                    processLoadingSave.value = false;
                    return;
                }
                if (data.code === 'BITMAP_PDF_NOT_ALLOWED') {
                    showBitmapNotAllowed(bitmapErrorTarget)
                    processLoadingSave.value = false;
                    return;
                }
                const messages = Object.values(data.errors || {}).flat();
                showToast(messages);
                processLoadingSave.value = false;
                return;
            }
            throw new Error("Upload failed");
        }

        const mediaData = await response.json()
        emitImageData(mediaData.media)
        emitImageColor()
        qualityCheckModalOpened.value = false
        cmykUploadWarningAccepted.value = false

    } catch (error) {
        console.warn(error);
    } finally {
        processLoadingSave.value = false;
    }
}
const isInvalidExtension = (photo) => {
    if (!photo?.file_name || !Array.isArray(props.extensions) || props.extensions.length === 0) {
        return false
    }
    const fileExtension = photo.file_name.slice((photo.file_name.lastIndexOf(".") - 1 >>> 0) + 1);
    const acceptedExtensions = props.extensions;
    return (!acceptedExtensions.includes(`${fileExtension.toLowerCase()}`));
}

const formattedAllowedExtensions = computed(() => {
    if (!Array.isArray(props.extensions) || props.extensions.length === 0) {
        return ''
    }
    return props.extensions.join(', ')
})

const invalidExtensionTooltipText = computed(() => {
    return invalidExtensionTooltipTemplate.value.replace('{extensions}', formattedAllowedExtensions.value)
})

const hideInvalidExtensionTooltip = () => {
    invalidExtensionTooltipVisible.value = false
}

const showInvalidExtensionTooltip = (event) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const tooltipEstimatedHeight = 72
    const placeBelow = rect.top < tooltipEstimatedHeight + 12
    invalidExtensionTooltipBelow.value = placeBelow
    invalidExtensionTooltipStyle.value = {
        top: placeBelow ? `${rect.bottom + 10}px` : `${rect.top - 10}px`,
        left: `${rect.left + rect.width / 2}px`,
        transform: placeBelow ? 'translate(-50%, 0)' : 'translate(-50%, -100%)'
    }
    invalidExtensionTooltipVisible.value = true
}

const onGalleryItemEnter = (event, image) => {
    if (isInvalidExtension(image)) {
        showInvalidExtensionTooltip(event)
    }
}

const selectPhoto = (photo) => {
    libraryGradientError.value = false

    if (isInvalidExtension(photo)) {
        return;
    }
    if (isSelected(photo)) {
        selectedImages.value = [];
        // selectedImages.value = selectedImages.value.filter(
        //     (selected) => selected.id !== photo.id,
        // )
        // TODO: get idea how many photos we can use
    } else if (selectedImages.value.length < 1) {
        selectedImages.value.push(photo)
    } else {
        selectedImages.value = [];
        selectedImages.value.push(photo)
    }
}

// const handleFakeUpload = () => {
//     // TODO: if needed. handle multiple
//     let image = selectedImages.value[0];
//     detectedColors.value = {
//         colors: (image && image.custom_properties && image.custom_properties.colors) ? image.custom_properties.colors : [],
//         file: 'test.svg'
//     };
//     emitImageData(image)
//     emitImageColor();
//     checkQualityFromLibrary.value = false;
//
// }

const cancelAddingImage = () => {
    resetUploadPreviews()
    hideToast()
}

const openUploadDrawer = () => {
    selectedImages.value = []
    libraryGradientError.value = false
    resetUploadPreviews()
    uploadVisible.value = true
}

const deleteImage = async () => {
    try {
        const response = await fetch("/api/user/files/" + imageIdToDelete.value, {
            method: "DELETE",
        });

        if (!response.ok) throw new Error("Delete failed");

    } catch (error) {
        console.warn(error);
    } finally {
        deleteImageModal.value = false
        await fetchImages();
    }
}

const handleDeleteClick = (event, image) => {
    event.stopPropagation();
    imageIdToDelete.value = image.id;
    deleteImageModal.value = true;
}

const emitImageData = (data) => {
    emit('update:imageData', data, true)
}

const emitImageColor = () => {
    emit('update:imageColors', detectedColors.value)
}

const qualityCheckMessage = computed(() => {
    if (qualityScore.value > 80) {
        return goodQualityMessage.value
    } else if (qualityScore.value <= 80 && qualityScore.value >= 30) {
        return okayQualityMessage.value
    } else {
        return badQualityMessage.value
    }
})

const qualityMark = computed(() => {
    if (qualityScore.value > 80) {
        return 'good'
    } else if (qualityScore.value <= 80 && qualityScore.value >= 30) {
        return 'okay'
    } else {
        return 'bad'
    }
})

const filteredImages = computed(() => {
    if (!searchQuery.value.trim()) {
        return images.value
    }
    const query = searchQuery.value.toLowerCase().trim()
    return images.value.filter(image =>
        image.name.toLowerCase().includes(query)
    )
})

const closeGalleryModal = () => {
    hideToast()
    libraryGradientError.value = false
    libraryBitmapError.value = false
    emit('close:gallery-modal')
}
const checkImageQuality = async () => {
    hideToast()
    if (uploadGradientError.value || uploadBitmapError.value) {
        return
    }
    if (isRasterLikeUpload(fileUpload.value, uploadIsBitmapPdf.value)) {
        detectedColors.value = {file: fileUpload.value.name, colors: []};
        colorProfile.value = null
        await handleUpload();
        return;
    }

    revokeBlobUrl(qualityCheckPreview.value)
    qualityCheckPreview.value = null
    processLoading.value = true;
    const formData = new FormData()
    formData.append('file', fileUpload.value)

    const urlParams = new URLSearchParams(window.location.search);


    let variantsArray = urlParams.get('variants');
    if (variantsArray) {
        variantsArray = variantsArray.replaceAll(" ", "").split(',');
    }else{
        variantsArray = [];
    }

    let erpCode = urlParams.get('erp_code') ?? 'V4201-19';
    if (variantsArray.length > 0) {
        erpCode = variantsArray[0];
    }


    const params = new URLSearchParams({
        erp_code: erpCode,
        print_place_name: urlParams.get('print_place_name') ?? 'panel 1',
        print_place_size: urlParams.get('print_place_size') ?? '200x120',
        print_method: urlParams.get('print_method') ?? 'TF1',
    });


    formData.append('erp_code', params.get('erp_code'))
    formData.append('print_place_name', params.get('print_place_name'))
    formData.append('print_place_size', params.get('print_place_size'))
    formData.append('print_method', params.get('print_method'))
    formData.append('uuid', props.uuid)

    printMethod.value = getCurrentPrintMethod()
    // formData.append('erp_code', params.value)
    try {
        const response = await fetch("/api/file/quality-check", {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            if (response.status === 422) {
                const data = await response.json();
                if (data.code === 'BITMAP_PDF_NOT_ALLOWED') {
                    showBitmapNotAllowed('upload')
                    return
                }
                if (data.code === 'BITMAP_PDF_RASTER') {
                    uploadIsBitmapPdf.value = true
                    detectedColors.value = {file: fileUpload.value.name, colors: []}
                    colorProfile.value = null
                    await handleUpload()
                    return
                }
            }
            throw new Error('Błąd podczas pobierania pliku');
        }

        const qualityHeader = response.headers.get("Quality");
        qualityScore.value = parseInt(qualityHeader);
        emit('update:quality-score', qualityScore.value);

        const colorsCount = response.headers.get("Colors-Count");
        logoColorsCount.value = parseInt(colorsCount);

        const blob = await response.blob(); // Przekształcenie strumienia na Blob

        // Tworzenie URL z Blob
        const url = URL.createObjectURL(blob);

        // Wyświetlanie obrazu
        qualityCheckPreview.value = url;

        await colorDetection();
        qualityCheckModalOpened.value = true

    } catch (error) {
        console.log(error);
    } finally {
        processLoading.value = false;
    }
}
const urlToFile = async (url, filename, mimeType) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, {type: mimeType});
}

const fetchProcessPreviewResult = async (file) => {
    printMethod.value = getCurrentPrintMethod()

    const formData = new FormData()
    formData.append('file', file)
    formData.append('uuid', props.uuid)
    formData.append('print_method', printMethod.value)

    const response = await fetch('/api/file/process-preview', {
        method: 'POST',
        body: formData,
    })

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()

    if (!result.success) {
        throw new Error(result.error || 'Błąd podczas przetwarzania pliku')
    }

    return result
}

const hasGradientError = (result) => !!result.isGradient && !isDigitalPrint.value

const processFileForPreview = async (file) => {
    try {
        // Nie ustawiamy processLoading.value tutaj, bo mamy dedykowany filePreviewLoading
        const result = await fetchProcessPreviewResult(file)

        uploadIsBitmapPdf.value = !!result.isBitmap
        if (hasBitmapBlockError(result)) {
            showBitmapNotAllowed('upload')
        } else {
            uploadBitmapError.value = false
        }

        // Jeśli jest gradient – pokazujemy czerwony komunikat,
        // ale nadal ustawiamy podgląd PDF.
        uploadGradientError.value = hasGradientError(result)
        filePreview.value = withCacheBuster(result.preview_url)
    } catch (error) {
        console.error('Błąd podczas wysyłania pliku do API:', error)
        // Fallback - użyj lokalnego URL jeśli API nie działa
        filePreview.value = URL.createObjectURL(file)
    } finally {
        // Wyłącz loader podglądu po zakończeniu procesu
        filePreviewLoading.value = false
    }
}

const libraryCheckImageQuality = async () => {
    hideToast()
    libraryGradientError.value = false
    libraryBitmapError.value = false
    uploadBitmapError.value = false
    colorProfile.value = null
    checkQualityFromLibrary.value = true
    processCheckQualityLoading.value = true

    const image = selectedImages.value[0]

    try {
        const file = await urlToFile(image.original_url, image.file_name, image.mime_type)
        fileUpload.value = file
        let previewIsBitmap = isBitmapPdfMedia(image)

        if (!isRasterImage(file) && previewIsBitmap && !allowsRasterUpload.value) {
            showBitmapNotAllowed('library')
            checkQualityFromLibrary.value = false
            return
        }

        if (!isRasterImage(file) && !previewIsBitmap) {
            const previewResult = await fetchProcessPreviewResult(file)
            previewIsBitmap = !!previewResult.isBitmap
            if (hasBitmapBlockError(previewResult)) {
                showBitmapNotAllowed('library')
                checkQualityFromLibrary.value = false
                return
            }
            if (hasGradientError(previewResult)) {
                libraryGradientError.value = true
                checkQualityFromLibrary.value = false
                return
            }
        }

        uploadIsBitmapPdf.value = previewIsBitmap

        if (isRasterLikeUpload(file, previewIsBitmap)) {
            detectedColors.value = {file: image.file_name, colors: []}
            colorProfile.value = null
            checkQualityFromLibrary.value = false
            await handleUpload('library')
            return
        }

        revokeBlobUrl(qualityCheckPreview.value)
        qualityCheckPreview.value = null
        processLoading.value = true
        const formData = new FormData()
        formData.append('file', fileUpload.value)

        const urlParams = new URLSearchParams(window.location.search)

        let variantsArray = urlParams.get('variants')
        if (variantsArray) {
            variantsArray = variantsArray.replaceAll(" ", "").split(',')
        } else {
            variantsArray = []
        }

        let erpCode = urlParams.get('erp_code') ?? 'V4201-19'
        if (variantsArray.length > 0) {
            erpCode = variantsArray[0]
        }

        const params = new URLSearchParams({
            erp_code: erpCode,
            print_place_name: urlParams.get('print_place_name') ?? 'panel 1',
            print_place_size: urlParams.get('print_place_size') ?? '200x120',
            print_method: urlParams.get('print_method') ?? 'TF1',
        })

        formData.append('erp_code', params.get('erp_code'))
        formData.append('print_place_name', params.get('print_place_name'))
        formData.append('print_place_size', params.get('print_place_size'))
        formData.append('print_method', params.get('print_method'))
        formData.append('uuid', props.uuid)
        printMethod.value = getCurrentPrintMethod()

        const response = await fetch("/api/file/quality-check", {
            method: "POST",
            body: formData,
        })

        if (!response.ok) {
            if (response.status === 422) {
                const data = await response.json()
                if (data.code === 'BITMAP_PDF_NOT_ALLOWED') {
                    showBitmapNotAllowed('library')
                    checkQualityFromLibrary.value = false
                    return
                }
            }
            throw new Error('Błąd podczas pobierania pliku')
        }

        const qualityHeader = response.headers.get("Quality")
        qualityScore.value = parseInt(qualityHeader)
        emit('update:quality-score', qualityScore.value)

        const colorsCount = response.headers.get("Colors-Count")
        logoColorsCount.value = parseInt(colorsCount)

        const blob = await response.blob()
        qualityCheckPreview.value = URL.createObjectURL(blob)

        await colorDetection()
        qualityCheckModalOpened.value = true
    } catch (error) {
        console.log(error)
    } finally {
        processLoading.value = false
        processCheckQualityLoading.value = false
    }
}

const colorDetection = async () => {
    detectedColors.value = [];
    colorProfile.value = null
    const urlParams = new URLSearchParams(window.location.search);


    let variantsArray = urlParams.get('variants');
    if (variantsArray) {
        variantsArray = variantsArray.replaceAll(" ", "").split(',');
    }else{
        variantsArray = [];
    }

    let erpCode = urlParams.get('erp_code') ?? 'V4201-19';
    if (variantsArray.length > 0) {
        erpCode = variantsArray[0];
    }

    const params = new URLSearchParams({
        erp_code: erpCode,
        print_place_name: urlParams.get('print_place_name') ?? 'panel 1',
        print_place_size: urlParams.get('print_place_size') ?? '200x120',
        print_method: urlParams.get('print_method') ?? 'TF1',
    });


    try {
        const response = await fetch('/api/file/color-detect?uuid=' + props.uuid + '&print_method=' + params.get('print_method') + '&print_place_name=' + params.get('print_place_name') + '&print_place_size=' + params.get('print_place_size') + '&erp_code=' + params.get('erp_code'))
        if (!response.ok) {
            throw new Error(`Status: ${response.status}`)
        }

        let colors = await response.json();

        detectedColors.value = {file: 'test.svg', colors: colors.colors};
        colorProfile.value = colors.color_profile ?? null
    } catch (error) {
        console.error('Error: ', error)
        colorProfile.value = null
    } finally {
        loadingImages.value = false
        processLoading.value = false;
    }
}

const emit = defineEmits([
    'update:imageData',
    'update:imageColors',
    'close:gallery-modal',
    'update:quality-score'
])


watch(() => props.userId, async (newUserId) => {
    if (newUserId) {
        currentUserId.value = newUserId
    }
}, {immediate: true})

watch(() => props.removeLogo, () => {
    resetUploadPreviews()
})

onMounted(async () => {
        galleryScrollContainer.value?.addEventListener('scroll', hideInvalidExtensionTooltip, {passive: true})
        await fetchUserIdFromVisualization()
        await fetchImages()

        libraryTitle.value = await $t('library.title', 'TWOJA BIBLIOTEKA')
        uploadNewFileText.value = await $t('library.upload_new_file', 'WGRAJ NOWY PLIK')
        uploadNewFileText2.value = await $t('upload.upload_new_file', 'Wgraj nowy plik')
        unsupportedFormatText.value = await $t('upload.unsupported_format', 'Nieobsługiwany format pliku!')
        uploadFileText.value = await $t('upload.upload_file', 'WGRAJ PLIK')
        orText.value = await $t('upload.or', 'lub')
        dropHereText.value = await $t('upload.drop_here', 'UPUŚĆ TUTAJ')
        maxFileSizeText.value = await $t('upload.max_file_size', 'Maksymalny rozmiar pliku')
        gradientErrorText.value = await $t('quality.gradient_error', 'Plik posiada gradient i nie może zostać przetworzony. Proszę wgrać inny plik.')
        bitmapErrorText.value = await $t('quality.bitmap_pdf_error', 'Plik PDF zawiera mapę bitową i nie może zostać przetworzony dla tej metody nadruku.')
        allowedFormatsText.value = await $t('upload.allowed_formats', 'Dozwolone formaty pliku')
        addToLibraryText.value = await $t('library.add_to_library', 'Dodaj plik do biblioteki')
        addImageText.value = await $t('buttons.add_image', 'DODAJ GRAFIKĘ')
        addImageText2.value = await $t('buttons.add_image', 'DODAJ GRAFIKĘ')
        addImageText3.value = await $t('buttons.add_image', 'DODAJ GRAFIKĘ')
        // addImageText.value = '111111111111'
        // addImageText2.value = '22222222222'
        // addImageText3.value = '3333333333333'
        cancelText.value = await $t('text.cancel', 'ANULUJ')
        cancelText2.value = await $t('text.cancel', 'ANULUJ')
        qualityCheckTitle.value = await $t('quality.checking_title', 'Sprawdzanie jakości pliku')
        backToLibraryText.value = await $t('library.back_to_library', 'WRÓĆ DO BIBLIOTEKI')
        badQualityText.value = await $t('quality.bad', 'Zła jakość')
        okayQualityText.value = await $t('quality.okay', 'Mogło być lepiej')
        goodQualityText.value = await $t('quality.good', 'Dobra jakość')
        goodQualityMessage.value = await $t('quality.good_message', 'Sprawdziliśmy jakość Twojego pliku i wszystko jest w porządku! Nie przewidujemy żadnych problemów podczas drukowania.')
        okayQualityMessage.value = await $t('quality.okay_message', 'Sprawdziliśmy jakość Twojego pliku – jest akceptowalna. Drukowanie nie powinno sprawić trudności, jednak dla uzyskania optymalnych rezultatów zalecamy przesłanie pliku o wyższej jakości.')
        badQualityMessage.value = await $t('quality.bad_message', 'Ta grafika zawiera zbyt wiele drobnych elementów (szczegóły wskazane poniżej). Zalecamy wgranie nowej grafiki, aby zapewnić lepsze rezultaty druku.')
        graverColorReplaceMessage.value = await $t('quality.grav_message', 'Ta grafika zawiera zbyt wiele drobnych elementów (szczegóły wskazane poniżej). Zalecamy wgranie nowej grafiki, aby zapewnić lepsze rezultaty druku.')
        cmykConversionMessage.value = await $t('quality.cmyk_conversion_message', 'Wgrane logo jest w innej kolorystyce niż CMYK i zostanie przekonwertowane na CMYK za pomocą programu graficznego, na co wyrażasz zgodę.')
        filterLogotypesText.value = await $t('library.filter_logotypes', 'Filtruj logotypy...')
        noFilesInLibraryText.value = await $t('library.no_files', 'Brak plików w bibliotece')
        noSearchResultsText.value = await $t('library.no_search_results', 'Brak wyników wyszukiwania')
        invalidExtensionTooltipTemplate.value = await $t(
            'library.invalid_extension_tooltip',
            'Dla wybranej metody nadruku dopuszczone rozszerzenia to: {extensions}'
        )
        deleteImageTitle.value = await $t('image.delete_title', 'Usuwanie grafiki')
        deleteImageMessage.value = await $t('image.delete_from_library_message', 'Czy na pewno chcesz usunąć grafikę z Twojej biblioteki?')
        deleteImageCancelText.value = await $t('image.cancel', 'Anuluj')
        deleteImageConfirmText.value = await $t('image.confirm_delete', 'Usuń')
        continueText.value = await $t('buttons.continue', 'KONTYNUUJ')
    }
)

</script>

<style scoped>
.right-drawer {
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    overflow: hidden;
    padding-left: 0;
    border-left: 1px solid whitesmoke;
    background: white;
    z-index: 9999;
}

.drawer-mask {
    position: absolute;
    left: 0;
    top: 0;
    width: 0; /* initially */
    background: #000;
    opacity: 0.3;
    z-index: 199;
}

.image-name-space {
    text-overflow: ellipsis;
}


.custom-spin {
    animation: spin 1s linear infinite;
}

.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 1200px) {
    .custom-line-height {
        line-height: 15px;
    }
}
</style>
